import { useCallback, useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  CustomAlert,
  CustomButton,
  CustomSelect,
  CustomTable,
  Text,
} from "../../../shared/uiComponents";
import { errorNormalizer } from "../../../shared/Helpers/functions";
import SignatureInput from "../../SOAPNote/components/signatureInput";
import { Headers, TableBody } from "./tableInfo";

import { DispatchProperties, useSelector } from "../../../redux/store";
import {
  approveSoapNotes,
  getPendingSoapNotes,
} from "../../../redux/State/clientSlice/soapNoteSlice";
import {
  ApprovePendingSoapNotesProperties,
  GetPendingSoapNotesProperties,
} from "../../../redux/API/ClientAPIHelpers/soapNoteProperties";
import { SelectChangeEvent } from "@mui/material";
import { getUserBTs } from "../../../redux/State/clientSlice/userClientSlice";

const PendingApproval = () => {
  const dispatch = useDispatch<DispatchProperties>();

  const [params, setParams] = useSearchParams();
  const btId = params.get("btId") || "";
  const selectAll = params.get("selectAll") === "true" ? true : false;
  const page = params.get("page") || "1";
  const pageSize = params.get("pageSize") || "8";
  const direction = params.get("direction") || "";
  const orderBy = params.get("orderBy") || "";

  const [open, setOpen] = useState<boolean>(false);

  const notes = useSelector((state) => state.soapNote.pendingSoapNotes);
  const loading = useSelector((state) => state.soapNote.loading);
  const user = useSelector((state) => state.account.user);
  const userBTs = useSelector((state) => state.userClient.userBTs);
  const reportIds = useSelector((state) => state.billing.billingFilters.reportIds);

  const openApproveHandler = () => setOpen(true);
  const closeApproveHandler = () => setOpen(false);

  useEffect(() => {
    const data: GetPendingSoapNotesProperties = {
      page,
      pageSize,
      direction,
      orderBy,
      userId: btId,
    };

    dispatch(getPendingSoapNotes(data));
  }, [dispatch, page, pageSize, direction, orderBy, btId]);

  useEffect(() => {
    if (!notes?.totalNumberOfItems || !user?.id) return;
    dispatch(getUserBTs(user.id));
  }, [notes, user, dispatch]);

  const selectBtHandler = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    if (!!value) {
      params.set("btId", value);
    } else {
      params.delete("btId");
    }
    setParams(params);
  };

  const labels = userBTs.map((bt) => ({ id: bt.id, name: bt.fullName }));
  const data = [...labels, { id: "", name: "Clear" }];

  return (
    <div style={{ width: "50%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
        }}
      >
        <Text title={"Pending SOAP Notes"} size="mediumBold" />
        <div style={{ width: "200px" }}>
          <CustomSelect data={data} value={btId} setValue={selectBtHandler} />
        </div>
        <div style={{ width: "max-content" }}>
          <CustomButton
            onClick={openApproveHandler}
            title={"Approve"}
            disabled={!reportIds.length && !selectAll}
          />
          <CustomAlert
            open={open}
            onClose={closeApproveHandler}
            title="Are you sure?"
            Content={({ onClose }) => <Content onClose={onClose} />}
          />
        </div>
      </div>
      <CustomTable
        headers={Headers}
        TableBody={TableBody}
        data={notes}
        loading={loading}
        hasMobile={false}
      />
    </div>
  );
};

const Content = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch<DispatchProperties>();
  const [params, setParams] = useSearchParams();

  const [signature, setSignature] = useState<string>("");

  const reportIds = useSelector((state) => state.billing.billingFilters.reportIds);
  const notes = useSelector((state) => state.soapNote.pendingSoapNotes);
  const loading = useSelector((state) => state.soapNote.loading);

  const approveHandler = useCallback(() => {
    const page = "1";
    const pageSize = !!notes.totalNumberOfItems ? notes.totalNumberOfItems.toString() : "8";
    const userId = params.get("userId") || "";
    const all = params.get("selectAll");

    const data: ApprovePendingSoapNotesProperties = {
      page,
      pageSize,
      signature,
      userId,
      reportIds: all === "true" ? [] : reportIds,
    };
    dispatch(approveSoapNotes(data))
      .then(unwrapResult)
      .then(() => {
        dispatch(getPendingSoapNotes(data));
        onClose();
        params.delete("selectAll");
        setParams(params);
        toast.success("SOAP Notes approved successfully");
      })
      .catch(errorNormalizer);
  }, [dispatch, onClose, setParams, params, notes, reportIds, signature]);

  return (
    <>
      <SignatureInput
        value={signature}
        setValue={({ target }) => setSignature(target.value.toUpperCase())}
        disabled={false}
        onBlur={() => {}}
        error={false}
      />
      <CustomButton
        onClick={approveHandler}
        title={"Approve"}
        loading={loading}
        disabled={!signature}
      />
    </>
  );
};

export default PendingApproval;
